import { JsonObject, millisecondsPer, object, objectWithout, randomString } from '@art-suite/art-core-ts';

import { getAge } from './ScanSynchronizer/syncLib';

export type SyncLogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

const SyncLogLevelToNumber: Record<SyncLogLevel, number> = {
  debug: 3,
  info: 2,
  warn: 1,
  error: 0,
  none: -1,
};

const SyncLogLevelToConsoleMethod: Record<SyncLogLevel, 'log' | 'info' | 'warn' | 'error'> = {
  debug: 'log',
  info: 'info',
  warn: 'warn',
  error: 'error',
  none: 'log',
};

export type SyncLoggerFunctionWithLevel = (
  level: SyncLogLevel,
  context: string,
  message: string,
  error?: Error | unknown,
  additionalContext?: JsonObject,
) => void;
export type SyncLoggerFunction = (context: string, message: string, error?: Error | unknown, additionalContext?: JsonObject) => void;

export type WithProgressFunction = <T>(progressLabel: string, doFunction: (onProgress: (progress: number) => void) => Promise<T>) => Promise<T>;

/** Callable as `syncLogger('info', ctx, msg)` or `syncLogger.info(ctx, msg)` */
export type SyncLogger =
  & SyncLoggerFunctionWithLevel
  & {
    [logLevel in SyncLogLevel]: SyncLoggerFunction;
  }
  & { withProgress: WithProgressFunction };

export type ProgressCallback = (
  progressId: string,
  progressLabel: string,
  progress: number | null, // null means clear/abort progress
) => void;

export type ProgressId = string;
export type ProgressDetails = {
  label: string;
  progress: number;
  firstProgressAt: Date;
  lastProgressAt: Date;
};
export type ActiveProgressTracker = Record<ProgressId, ProgressDetails>;

const MAX_PROGRESS_TRACKER_AGE_MS = millisecondsPer.minute * 10;

export const getUpdatedProgressTracker = (activeProgressTracker: ActiveProgressTracker, progressId: ProgressId, progressDetails?: ProgressDetails) => {
  const nowNumber = Date.now();
  activeProgressTracker = object(activeProgressTracker, { when: entry => getAge(entry.lastProgressAt, nowNumber) < MAX_PROGRESS_TRACKER_AGE_MS });
  if (progressDetails && progressDetails.progress < .995) {
    return {
      ...activeProgressTracker,
      [progressId]: progressDetails,
    };
  }
  return objectWithout(activeProgressTracker, progressId);
};

export type SetProgressTracker = (progressTrackerTransform: (activeProgressTracker: ActiveProgressTracker) => ActiveProgressTracker) => unknown;

export const createSyncLogger = (
  envLogLevel: SyncLogLevel,
  auxiliaryLogger?: SyncLoggerFunctionWithLevel,
  setProgressTracker?: SetProgressTracker,
): SyncLogger => {
  const log: SyncLoggerFunctionWithLevel = (level, context, message, error, additionalContext) => {
    auxiliaryLogger?.(level, context, message, error, additionalContext);
    if (SyncLogLevelToNumber[envLogLevel] < SyncLogLevelToNumber[level]) return;
    if (level === 'error') {
      console.error(message, error, { level, context, ...additionalContext });
    } else {
      console[SyncLogLevelToConsoleMethod[level]](`[ScanSynchronizer ${context}] (${level}) ${message}`, error, additionalContext);
    }
  };
  const progressCallback = (progressId: ProgressId, progressLabel: string, progress: number | null) =>
    setProgressTracker?.(activeProgressTracker =>
      getUpdatedProgressTracker(
        activeProgressTracker,
        progressId,
        progress != null && progress < .995
          ? {
            label: progressLabel,
            progress,
            firstProgressAt: activeProgressTracker[progressId]?.firstProgressAt ?? new Date(),
            lastProgressAt: new Date(),
          }
          : undefined,
      )
    );

  const withProgress: WithProgressFunction = async <T>(progressLabel: string, doFunction: (onProgress: (progress: number) => void) => Promise<T>) => {
    const progressId = randomString();
    progressCallback(progressId, progressLabel, 0);
    try {
      return await doFunction((progress: number) => progressCallback(progressId, progressLabel, progress));
    } finally {
      progressCallback(progressId, progressLabel, null);
    }
  };
  return Object.assign(log, {
    debug: log.bind(null, 'debug'),
    info: log.bind(null, 'info'),
    warn: log.bind(null, 'warn'),
    error: log.bind(null, 'error'),
    none: log.bind(null, 'none'),
    withProgress,
  }) as SyncLogger;
};
